import React, { useState, useEffect, useMemo } from 'react';
import { Switch, Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UsersIcon,
  PlusIcon,
  LockClosedIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Spin } from 'antd';
import { TableRowsSkeleton } from '../../../components/skeleton/skeleton';
import ModalDelete from '../../../components/modal-delete/modal-delete';
import Notification from '../../../components/notification/notification';
import roleApi from '../../../services/roleApi';
import { PageHeader, TWButton, TWModal } from '../../../components/ui';
import './permission.css';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const ROLE_ACCENTS = [
  'from-blue-500 to-sky-500',
  'from-violet-500 to-purple-500',
  'from-emerald-500 to-teal-500',
  'from-rose-500 to-pink-500',
  'from-amber-500 to-orange-500',
  'from-cyan-500 to-blue-500',
  'from-fuchsia-500 to-pink-500',
  'from-indigo-500 to-violet-500',
];

const getRoleAccent = (name = '') => {
  const hash = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return ROLE_ACCENTS[hash % ROLE_ACCENTS.length];
};

const getRoleInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0])
    .join('')
    .toUpperCase();

const Permission = () => {
  const [roleList, setRoleList] = useState([]);
  const [permissionList, setPermissionList] = useState([]);
  const [loading, setLoading] = useState({
    initialLoading: true,
    roleLoading: false,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [errors, setErrors] = useState({
    name: false,
  });
  const [form, setForm] = useState({
    id: '',
    name: '',
    permissions: [],
    isAdmin: false,
  });
  const [query, setQuery] = useState('');
  const [deletedItem] = useState({ name: '', id: '' });
  const [dialogs, setDialogs] = useState({
    upsert: false,
    delete: false,
  });
  const [search, setSearch] = useState('');

  const getRoleList = async (isPermissionSetting = false) => {
    try {
      if (!isPermissionSetting) {
        setLoading((prev) => ({ ...prev, initialLoading: true }));
      }
      const list = await roleApi.roleList();
      setRoleList(list.data.data || []);
    } catch (error) {
      setNotf({ display: true, type: 'error', message: error });
    }
  };

  const getPermissionList = async () => {
    try {
      const list = await roleApi.permissionList();
      setPermissionList(list.data.data || []);
    } catch (error) {
      setNotf({ display: true, type: 'error', message: error });
    } finally {
      setLoading((prev) => ({ ...prev, initialLoading: false }));
    }
  };

  const changeValue = async (roleId, permissionId, checked) => {
    try {
      setLoading((prev) => ({ ...prev, roleLoading: true }));
      if (checked) {
        await roleApi.addPermissionToRole(roleId, permissionId);
      } else {
        await roleApi.removePermissionToRole(roleId, permissionId);
      }
      await getRoleList(true);
    } catch (error) {
      setNotf({ display: true, type: 'error', message: 'Update failed' });
    } finally {
      setLoading((prev) => ({ ...prev, roleLoading: false }));
    }
  };

  const filteredPermissions = useMemo(
    () =>
      query === ''
        ? permissionList
        : permissionList.filter((p) =>
            p.name?.toLowerCase().includes(query.toLowerCase()),
          ),
    [permissionList, query],
  );

  const visiblePermissions = useMemo(
    () =>
      permissionList.filter((p) =>
        p.name?.toLowerCase().includes(search.toLowerCase()),
      ),
    [permissionList, search],
  );

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickAddRole = () => {
    const maxCode = roleList.reduce(
      (max, role) => Math.max(max, Number(role.code) || 0),
      100,
    );
    setForm({
      id: '',
      name: '',
      permissions: [],
      isAdmin: false,
      code: maxCode + 50,
    });
    setQuery('');
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await roleApi.deleteRoleById(deletedItem.id);
      await getRoleList(true);
    } catch (error) {
      setNotf({ display: true, type: 'error', message: 'Delete failed' });
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const onFormSubmit = async () => {
    if (!form.name) {
      setErrors((prev) => ({ ...prev, name: true }));
      return;
    }
    setErrors((prev) => ({ ...prev, name: false }));
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...form };
      if (form.isAdmin) body.code = 100;

      const res = await roleApi.upsertRole(body);
      if (res.data.data) {
        setDialogs((prev) => ({ ...prev, upsert: false }));
      }
    } catch (error) {
      setNotf({ display: true, type: 'error', message: 'Save failed' });
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getRoleList(true);
    }
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({ ...prev, name: false }));
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const isEnabled = (roleId, permissionId) => {
    const found = roleList?.find((role) => role.id === roleId);
    return Boolean(
      found?.rolepermissions?.find(
        (permission) => permission.permissionid === permissionId,
      ),
    );
  };

  const getRolePermissionCount = (role) => role?.rolepermissions?.length || 0;

  useEffect(() => {
    getRoleList();
    getPermissionList();
  }, []);

  const totalPermissions = permissionList.length;

  return (
    <div className="permission-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Roles & Permissions"
          description="Control what each role can do across the platform."
          actions={
            <>
              {loading.roleLoading && (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-sky-500/10 dark:text-sky-400">
                  <Spin size="small" />
                  Saving…
                </span>
              )}
              <TWButton
                variant="primary"
                icon={PlusIcon}
                label="Add Role"
                disabled={loading.initialLoading}
                onClick={onClickAddRole}
              />
            </>
          }
        />

        {loading.initialLoading ? (
          <div className="reveal rounded-md border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800/70">
            <TableRowsSkeleton count={6} />
          </div>
        ) : (
          <>
            <section className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
              <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-700/60">
                <div className="flex items-center gap-2">
                  <UsersIcon className="h-5 w-5 text-slate-400" />
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    Roles
                  </h3>
                  <span className="rounded-md bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    {roleList.length}
                  </span>
                </div>
              </header>
              {roleList.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                  <UsersIcon className="h-10 w-10 text-slate-300" />
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    No roles yet
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Create a role to start assigning permissions.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {roleList.map((role) => {
                    const count = getRolePermissionCount(role);
                    const pct = totalPermissions
                      ? Math.round((count / totalPermissions) * 100)
                      : 0;
                    const isAdmin = role.code === 100;
                    return (
                      <div
                        key={role.id}
                        className="group relative rounded-md border border-slate-200 bg-white p-4 transition hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:bg-slate-800/60 dark:hover:border-slate-600"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className={classNames(
                                'flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gradient-to-br text-xs font-bold text-white shadow-sm',
                                getRoleAccent(role.name),
                              )}
                            >
                              {getRoleInitials(role.name)}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {role.name}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                Code {role.code}
                              </p>
                            </div>
                          </div>
                          {isAdmin && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-400/30">
                              <SparklesIcon className="h-3 w-3" />
                              Admin
                            </span>
                          )}
                        </div>
                        <div className="mt-3 space-y-1.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500 dark:text-slate-400">
                              Permissions
                            </span>
                            <span className="font-semibold text-slate-700 dark:text-slate-200">
                              {isAdmin ? totalPermissions : count} /{' '}
                              {totalPermissions}
                            </span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-sky-500 transition-all"
                              style={{
                                width: `${isAdmin ? 100 : pct}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
              <header className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-700/60 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-5 w-5 text-slate-400" />
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    Permission Matrix
                  </h3>
                  <span className="rounded-md bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    {visiblePermissions.length} / {totalPermissions}
                  </span>
                </div>
                <div className="relative flex items-center sm:w-72">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search permissions…"
                    className="w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20"
                  />
                </div>
              </header>

              {visiblePermissions.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                  <LockClosedIcon className="h-10 w-10 text-slate-300" />
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    No permissions match
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Try a different search term.
                  </p>
                </div>
              ) : (
                <div className="max-h-[70vh] overflow-auto">
                  <table className="permission-matrix min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="sticky-col w-64 min-w-[220px] px-5 py-3 text-left text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                          Permission
                        </th>
                        {roleList.map((role) => {
                          const count = getRolePermissionCount(role);
                          return (
                            <th
                              key={role.id}
                              className="min-w-[140px] px-3 py-3 text-center text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400"
                            >
                              <div className="flex flex-col items-center gap-1">
                                <span className="truncate text-xs font-semibold text-slate-800 dark:text-slate-200">
                                  {role.name}
                                </span>
                                <span className="text-[0.6rem] font-medium text-slate-400">
                                  {count}/{totalPermissions}
                                </span>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                      {visiblePermissions.map((permission) => (
                        <tr key={permission.id}>
                          <td className="sticky-col whitespace-nowrap px-5 py-3 text-sm font-medium text-slate-700 dark:text-slate-200">
                            {permission.name}
                          </td>
                          {roleList.map((role) => {
                            const enabled = isEnabled(role.id, permission.id);
                            return (
                              <td
                                key={role.id}
                                className="px-3 py-3 text-center"
                              >
                                <Switch
                                  checked={enabled}
                                  disabled={loading.roleLoading}
                                  onChange={(e) =>
                                    changeValue(role.id, permission.id, e)
                                  }
                                  className={classNames(
                                    enabled
                                      ? 'bg-blue-600 dark:bg-sky-500'
                                      : 'bg-slate-200 dark:bg-slate-600',
                                    loading.roleLoading && 'opacity-60',
                                    'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:ring-offset-1 disabled:cursor-wait',
                                  )}
                                >
                                  <span
                                    aria-hidden="true"
                                    className={classNames(
                                      enabled
                                        ? 'translate-x-4'
                                        : 'translate-x-0',
                                      'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                    )}
                                  />
                                </Switch>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
      <ModalDelete
        title="Delete Role"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedItem}
      />

      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title="Create Role"
        description="Define a new role and its permissions."
        size="xl"
        showCloseButton
        dismissible={false}
        panelClassName="overflow-visible dark:text-slate-200"
        bodyClassName="p-0"
        footer={
          <div className="flex items-center justify-end gap-2">
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading.upsertLoading}
              onClick={onCloseUpsert}
            />
            <TWButton
              variant="primary"
              label="Add Role"
              disabled={loading.upsertLoading}
              loading={loading.upsertLoading}
              onClick={onFormSubmit}
            />
          </div>
        }
      >
        <div className="space-y-4 px-6 py-5">
          <div>
            <label
              htmlFor="rolename"
              className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400"
            >
              Role Name
            </label>
            <input
              id="rolename"
              value={form.name}
              onChange={(e) => onFormChange(e.target.value, 'name')}
              type="text"
              placeholder="Store Manager"
              className={classNames(
                'block w-full rounded-md border bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500',
                errors.name
                  ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20 dark:border-rose-400/50'
                  : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-700 dark:focus:border-sky-400 dark:focus:ring-sky-400/20',
              )}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-rose-600">
                Role Name can not be empty
              </p>
            )}
          </div>

          <div>
            <Combobox
              as="div"
              multiple
              value={form.permissions}
              onChange={(val) => onFormChange(val, 'permissions')}
              disabled={form.isAdmin}
            >
              <Combobox.Label className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                Permissions
              </Combobox.Label>
              <div className="relative">
                <Combobox.Input
                  className={classNames(
                    'block w-full rounded-md border border-slate-200 bg-white px-3 py-2 pr-10 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-sky-400 dark:focus:ring-sky-400/20',
                    form.isAdmin && 'opacity-50',
                  )}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={
                    form.isAdmin
                      ? 'Admin has all permissions'
                      : 'Select permissions…'
                  }
                  displayValue={(permission) =>
                    filteredPermissions
                      .filter((x) => permission.includes(x.id))
                      .map((x) => x.name)
                      .join(', ')
                  }
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none">
                  <ChevronUpDownIcon
                    className="h-5 w-5 text-slate-400"
                    aria-hidden="true"
                  />
                </Combobox.Button>
                {filteredPermissions.length > 0 && !form.isAdmin && (
                  <Combobox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg focus:outline-none dark:border-slate-700 dark:bg-slate-900">
                    {filteredPermissions.map((permission) => (
                      <Combobox.Option
                        key={permission.id}
                        value={permission.id}
                        className={({ active }) =>
                          classNames(
                            'relative cursor-pointer select-none py-2 pl-3 pr-9',
                            active
                              ? 'bg-blue-50 text-blue-700 dark:bg-sky-500/10 dark:text-sky-400'
                              : 'text-slate-700 dark:text-slate-200',
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={classNames(
                                'block truncate',
                                selected && 'font-semibold',
                              )}
                            >
                              {permission.name}
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600 dark:text-sky-400">
                                <CheckIcon
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                )}
              </div>
            </Combobox>
            {form.permissions?.length > 0 && !form.isAdmin && (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {form.permissions.length} selected
              </p>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 bg-slate-50/50 p-3 transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900/50 dark:hover:border-slate-600">
            <input
              type="checkbox"
              checked={form.isAdmin}
              onChange={(e) => onFormChange(e.target.checked, 'isAdmin')}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                Make this an Admin role
              </p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                Admin roles automatically have every permission in the system.
              </p>
            </div>
          </label>
        </div>
      </TWModal>
    </div>
  );
};

export default Permission;
